/**
 * officialTimeModel — GDN-2510 official session-time model.
 *
 * Pure resolution + write-payload logic for the official time selector: which
 * clock stamps the note & claim. Three sources:
 *
 *   - in_room:   the recording moment. For a dictation this is not the visit —
 *                surfaced as "Time of dictation" and de-emphasized. Resolved
 *                from recording metadata (`recordingWindow`) first; the
 *                encounter clock only counts while it still IS the recording
 *                clock (see resolveRecordingTimes — the clock is this
 *                selector's OUTPUT, so once another source wrote it, reading
 *                it back as "the recording time" would be an echo).
 *   - scheduled: the linked appointment slot (start_time + duration).
 *   - manual:    provider-entered date/start/duration.
 *
 * WRITE SEMANTICS (backward compat + calendar safety):
 *   - Patches contain ONLY start_time / end_time / date_of_service /
 *     time_source. NEVER scheduled_for — that field syncs to the appointment
 *     and editing the official time must never move the calendar slot.
 *   - Choosing "Scheduled" COPIES the slot's times into start_time/end_time;
 *     it does not link or move anything.
 *   - time_source is written only at a real decision point (user choice or the
 *     one dictation-with-appointment auto-default). An untouched full
 *     recording keeps time_source null — the legacy behavior recorders
 *     (iOS/extension) rely on.
 */

import { parseUTCDate } from '@utils/dateUtils';

export type OfficialTimeSource = 'in_room' | 'scheduled' | 'manual';

export interface OfficialTimeAppointmentLike {
  start_time?: string | null;
  /** Minutes. */
  duration?: number | null;
}

/** Structural subset of EncounterResponse the selector needs. */
export interface OfficialTimeEncounterLike {
  start_time?: string | null;
  end_time?: string | null;
  date_of_service?: string | null;
  service_date?: string | null;
  time_source?: OfficialTimeSource | null;
  appointment?: OfficialTimeAppointmentLike | null;
}

/**
 * The exact shape of an official-time PATCH. Deliberately closed: no
 * scheduled_for key can ever ride along.
 */
export interface OfficialTimePatch {
  start_time?: string;
  end_time?: string;
  /** Date-only "YYYY-MM-DD" billing day. */
  date_of_service?: string;
  time_source: OfficialTimeSource;
}

/** Manual editor value — date + start + duration (providers think in visit lengths). */
export interface ManualTimeValue {
  /** yyyy-MM-dd */
  date: string;
  /** HH:mm; empty until the user picks. */
  start: string;
  /** Minutes; null until the user picks. */
  durationMin: number | null;
}

/** Duration presets shared with the session popover's duration row. */
export const DURATION_PRESETS = [10, 20, 30, 60];

// ---------------------------------------------------------------------------
// Local date/time formatting — all in the user's local timezone.
// ---------------------------------------------------------------------------

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Date → local "YYYY-MM-DD". */
export const toLocalDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Date → local "HH:mm". */
export const toLocalTimeString = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** Local-calendar today as "YYYY-MM-DD". */
export const todayLocalDate = (): string => toLocalDateString(new Date());

/** '13:30' → '1:30 PM'. */
export const formatClock = (hhmm: string): string => {
  const [h, min] = hhmm.split(':').map(n => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(min)) return hhmm;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(min).padStart(2, '0')} ${suffix}`;
};

/** '2026-05-19' → 'May 19, 2026'. */
export const formatDateLong = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return isoDate;
  return `${MONTHS_SHORT[m - 1]} ${d}, ${y}`;
};

/** '2026-05-19' → 'May 19'. */
export const formatDateShort = (isoDate: string): string => {
  const [, m, d] = isoDate.split('-').map(n => parseInt(n, 10));
  if (!m || !d) return isoDate;
  return `${MONTHS_SHORT[m - 1]} ${d}`;
};

/**
 * "1:29–1:36 PM" — compact clock range; the start's meridian is dropped when
 * the end carries the same one.
 */
export const formatClockRange = (start: Date, end: Date | null): string => {
  const startClock = formatClock(toLocalTimeString(start));
  if (!end) return startClock;
  const endClock = formatClock(toLocalTimeString(end));
  const sameMeridian = startClock.slice(-2) === endClock.slice(-2);
  const startPart = sameMeridian ? startClock.replace(/ (AM|PM)$/, '') : startClock;
  return `${startPart}–${endClock}`;
};

/** start "HH:mm" + duration → "2:00 PM"; null when either is missing/invalid. */
export const computeEndClock = (start: string, durationMin: number | null): string | null => {
  if (!start || !durationMin) return null;
  const [h, m] = start.split(':').map(n => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const total = h * 60 + m + durationMin;
  const endH = Math.floor(total / 60) % 24;
  const endM = total % 60;
  return formatClock(`${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`);
};

// ---------------------------------------------------------------------------
// Source resolution — what each source's concrete times are.
// ---------------------------------------------------------------------------

export interface ResolvedTimes {
  start: Date;
  end: Date | null;
}

/**
 * The true recording moment, sourced from recording metadata — live recorder
 * state, the recording document(s) on the encounter, or the note's
 * transcription timestamps — never from the encounter clock.
 */
export interface RecordingWindow {
  start: Date;
  end: Date | null;
}

/**
 * Parse a SERVER-ORIGIN timestamp. These strings are frequently naive (no
 * 'Z'/offset suffix — PyMongo datetimes and `timestamp` columns serialize
 * without one) but always denote UTC instants. A bare `new Date(iso)` would
 * read a naive string as LOCAL wall time and shift every displayed clock by
 * the user's UTC offset (the founder repro: 5:45 PM ET rendered as 9:45 PM).
 * parseUTCDate is suffix-aware: with a suffix it parses as-is, without one it
 * appends 'Z'.
 */
const parseDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = parseUTCDate(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The encounter clock — the OFFICIAL times (whatever source last wrote them). */
export const resolveInRoomTimes = (
  encounter: OfficialTimeEncounterLike,
): ResolvedTimes | null => {
  const start = parseDate(encounter.start_time);
  if (!start) return null;
  return { start, end: parseDate(encounter.end_time) };
};

/**
 * The recording window — what the "Time of dictation" / "In room" row shows
 * and writes. The encounter clock is the selector's OUTPUT; it may only double
 * as the recording SOURCE while it still IS the recording clock:
 *
 *   1. `recordingWindow` metadata when provided (live recorder state, the
 *      recording document(s) on the encounter, or the note's transcription
 *      timestamps) — always wins; it survives the clock being overwritten.
 *   2. The encounter clock, ONLY when time_source is null/legacy (clock
 *      untouched = genuinely the recording times) or 'in_room' (an in_room
 *      write is by definition a copy of the recording times).
 *   3. null — a scheduled/manual choice overwrote the clock and no recording
 *      metadata is recoverable. There is no honest value: the row must be
 *      omitted rather than echo whatever source was last applied.
 */
export const resolveRecordingTimes = (
  encounter: OfficialTimeEncounterLike,
  recordingWindow?: RecordingWindow | null,
): ResolvedTimes | null => {
  if (recordingWindow?.start) {
    return { start: recordingWindow.start, end: recordingWindow.end ?? null };
  }
  if (!encounter.time_source || encounter.time_source === 'in_room') {
    return resolveInRoomTimes(encounter);
  }
  return null;
};

/** The linked appointment slot: start_time + duration. Null when unlinked. */
export const resolveScheduledTimes = (
  encounter: OfficialTimeEncounterLike,
): ResolvedTimes | null => {
  const start = parseDate(encounter.appointment?.start_time);
  if (!start) return null;
  const durationMin = encounter.appointment?.duration ?? null;
  const end = durationMin ? new Date(start.getTime() + durationMin * 60 * 1000) : null;
  return { start, end };
};

/** "1:29–1:36 PM" for a resolved source; null when the source is unavailable. */
export const formatResolvedTimes = (times: ResolvedTimes | null): string | null =>
  times ? formatClockRange(times.start, times.end) : null;

/**
 * Which source the selector should display.
 *
 * A stored time_source always wins. With none stored (legacy/null), the
 * readout is FACTUAL, not a choice: a full recording with a clock reads as
 * in-room, an appointment-backed encounter reads as scheduled. A dictation
 * with nothing stored is the ASK state (null) — the dictation clock is almost
 * never the visit time, so nothing is silently implied.
 */
export const getEffectiveSource = (
  encounter: OfficialTimeEncounterLike,
  isDictation: boolean,
): OfficialTimeSource | null => {
  if (encounter.time_source) return encounter.time_source;
  if (isDictation) return null;
  if (encounter.start_time) return 'in_room';
  if (encounter.appointment?.start_time) return 'scheduled';
  return null;
};

// ---------------------------------------------------------------------------
// Write payloads — the ONLY fields an official-time write may touch.
// ---------------------------------------------------------------------------

/** Options shared by the patch builders. */
interface BuildPatchOptions {
  /**
   * Whether the patch carries date_of_service. The recording screen resolves
   * the full date+time; the note popover's selector sets the time of day only
   * (the Date of Service field owns the billing date there).
   */
  includeDateOfService: boolean;
}

/**
 * Write the recording window to the clock; record the in-room decision. With
 * `recordingWindow` metadata this round-trips even after a scheduled/manual
 * choice overwrote the clock; without it, it degrades to the clock only while
 * the clock still is the recording clock (null when nothing honest exists —
 * the caller must not offer the option then).
 */
export const buildInRoomPatch = (
  encounter: OfficialTimeEncounterLike,
  { includeDateOfService, recordingWindow }: BuildPatchOptions & {
    recordingWindow?: RecordingWindow | null;
  },
): OfficialTimePatch | null => {
  const times = resolveRecordingTimes(encounter, recordingWindow);
  if (!times) return null;
  const patch: OfficialTimePatch = {
    time_source: 'in_room',
    start_time: times.start.toISOString(),
  };
  if (times.end) patch.end_time = times.end.toISOString();
  if (includeDateOfService) patch.date_of_service = toLocalDateString(times.start);
  return patch;
};

/**
 * Copy the appointment slot's times into the encounter clock. NEVER writes
 * scheduled_for — that would move the calendar slot.
 */
export const buildScheduledPatch = (
  encounter: OfficialTimeEncounterLike,
  { includeDateOfService }: BuildPatchOptions,
): OfficialTimePatch | null => {
  const times = resolveScheduledTimes(encounter);
  if (!times) return null;
  const patch: OfficialTimePatch = {
    time_source: 'scheduled',
    start_time: times.start.toISOString(),
  };
  if (times.end) patch.end_time = times.end.toISOString();
  if (includeDateOfService) patch.date_of_service = toLocalDateString(times.start);
  return patch;
};

export const isManualComplete = (manual: ManualTimeValue): boolean =>
  Boolean(manual.date && manual.start && manual.durationMin && manual.durationMin > 0);

/**
 * Provider-entered times. The recording-screen editor carries its own date;
 * the note-popover editor is time-of-day only and anchors to `anchorDate`
 * (the Date of Service) without writing date_of_service itself.
 */
export const buildManualPatch = (
  manual: ManualTimeValue,
  options: BuildPatchOptions & { anchorDate?: string },
): OfficialTimePatch | null => {
  if (!isManualComplete(manual)) return null;
  const date = options.includeDateOfService ? manual.date : (options.anchorDate ?? manual.date);
  // Intentionally a bare `new Date` (NOT parseUTCDate): the provider typed a
  // wall-clock time in their OWN timezone, so the local interpretation of the
  // suffix-less string is exactly right here.
  const start = new Date(`${date}T${manual.start}`);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + (manual.durationMin as number) * 60 * 1000);
  const patch: OfficialTimePatch = {
    time_source: 'manual',
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  };
  if (options.includeDateOfService) patch.date_of_service = manual.date;
  return patch;
};

/**
 * Seed for a just-opened manual editor: the current official times when
 * present, else the scheduled slot, else today with nothing chosen.
 */
export const seedManualValue = (encounter: OfficialTimeEncounterLike): ManualTimeValue => {
  const current = resolveInRoomTimes(encounter) ?? resolveScheduledTimes(encounter);
  if (current) {
    const durationMin = current.end
      ? Math.round((current.end.getTime() - current.start.getTime()) / 60000)
      : null;
    return {
      date: toLocalDateString(current.start),
      start: toLocalTimeString(current.start),
      durationMin: durationMin && durationMin > 0 ? durationMin : null,
    };
  }
  return { date: todayLocalDate(), start: '', durationMin: null };
};

/**
 * The one auto-default that acts (GDN-2510): a dictation with a linked
 * appointment defaults to the scheduled visit time when the recording save
 * completes. Returns the PATCH to apply, or null when the default must not
 * fire:
 *
 *   - full recording (>= threshold): the clock seeding is the existing legacy
 *     behavior; time_source stays null (no decision was made).
 *   - provider already chose a source: never overwrite a decision.
 *   - no linked appointment (ad-hoc dictation): ASK state; nothing is written
 *     until the provider chooses. DOS keeps its seed meanwhile.
 */
export const buildDictationScheduledDefaultPatch = (
  encounter: OfficialTimeEncounterLike,
  {
    isFullRecording,
    hasFullRecordingOnEncounter,
  }: { isFullRecording: boolean; hasFullRecordingOnEncounter: boolean },
): OfficialTimePatch | null => {
  if (isFullRecording) return null;
  if (hasFullRecordingOnEncounter) return null;
  if (encounter.time_source) return null;
  // GDN-2784: never default a dictation to a FUTURE scheduled slot. The slot
  // being days out means the provider is documenting outside the appointment
  // (usually today's visit landing in a pre-created future encounter) — the
  // auto-default was the amplifier that stamped 8/7 onto a 7/31 dictation.
  // Same-day slots keep the existing behavior.
  if (isScheduledForFutureDay(encounter)) return null;
  return buildScheduledPatch(encounter, { includeDateOfService: true });
};

/**
 * The encounter's visit day as a local "YYYY-MM-DD", or null when unknowable.
 * The linked appointment slot is the primary signal; the billing
 * date_of_service is the fallback for slotless encounters.
 */
export const visitDayLocalDate = (
  encounter: OfficialTimeEncounterLike,
): string | null => {
  const slot = parseDate(encounter.appointment?.start_time);
  if (slot) return toLocalDateString(slot);
  return encounter.date_of_service ?? null;
};

/**
 * Whether the encounter's visit day is in the FUTURE relative to the user's
 * local calendar (GDN-2784 recording/sign guards). Same-day and past days
 * return false — late same-day documentation is the dominant legitimate flow
 * and must never prompt.
 */
export const isScheduledForFutureDay = (
  encounter: OfficialTimeEncounterLike,
  today: string = todayLocalDate(),
): boolean => {
  const day = visitDayLocalDate(encounter);
  // Date-only ISO strings; lexical compare is date order.
  return day !== null && day > today;
};
